/**
 * ContratsDashboard — Cockpit Contrats en 3 zones (X4 refonte).
 *
 * Zone 1 (🔴 À traiter maintenant) : reconductions imminentes + indexations
 *   à réviser + dérive facturation + top 5 contrats à surveiller.
 *
 * Zone 2 (🔵 Pilotage) : KPI portefeuille (actifs, MRR, ARR), pie chart
 *   répartition par type (absorbé de ContratsPortefeuille), top 10 tiers par
 *   MRR (absorbé de ContratsFinancier).
 *
 * Zone 3 (⚫ Tendance & Évolution) : chart MRR 12m (multi-lignes clients/
 *   fournisseurs/net) + mouvements créations vs résiliations (absorbé de
 *   ContratsEvolution).
 *
 * Toutes les pages source (Portefeuille / Financier / Évolution) restent
 * fonctionnelles via routes -legacy si deep-link nécessaire.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp, RefreshCw, PieChart as PieIcon, Euro, Users, Loader2,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  LineChart, Line, ComposedChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend, PieChart, Pie, Cell,
} from "recharts";
import RenewalAlertsWidget from "@/components/RenewalAlertsWidget";
import IndexationAlertsBanner from "@/components/IndexationAlertsBanner";
import BillingDriftWidget from "@/components/BillingDriftWidget";
import { AutoLinkAllButton } from "@/components/AutoLinkAllButton";
import { AnalyzeAllButton } from "@/components/contracts/AnalyzeAllButton";

const fmtEUR = (v) => new Intl.NumberFormat("fr-FR",
  { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(v || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR") : "—";
const COLORS = ["#3b82f6", "#06b6d4", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6"];

// ─── ZoneTitle — barre latérale colorée + label majuscule uniforme
const ZoneTitle = ({ color, label, sub }) => (
  <div className="flex items-center gap-2 mb-3">
    <div className={`h-6 w-1 rounded-full bg-${color}-500`} />
    <h2 className="text-xs font-bold uppercase tracking-widest text-slate-700">
      {label}
    </h2>
    {sub && <span className="text-xs text-slate-400">· {sub}</span>}
  </div>
);

export default function ContratsDashboard() {
  const [overview, setOverview] = useState({});
  const [financial, setFinancial] = useState({});
  const [kpi, setKpi] = useState({});
  const [portfolio, setPortfolio] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [evolution, setEvolution] = useState([]);
  const [driftKey, setDriftKey] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [ov, fi, ki, pf, tl, ev] = await Promise.all([
          api.get("/contracts/stats/overview"),
          api.get("/contracts/stats/financial"),
          api.get("/contracts/kpi"),
          api.get("/contracts/stats/portfolio"),
          api.get("/contracts/stats/mrr-timeline?months=12"),
          api.get("/contracts/stats/evolution?months=12"),
        ]);
        setOverview(ov.data);
        setFinancial(fi.data);
        setKpi(ki.data);
        setPortfolio(pf.data);
        setTimeline(tl.data.timeline || []);
        setEvolution(ev.data.timeline || []);
      } catch (e) { toast.error(apiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-slate-500" data-testid="contrats-dashboard-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement du cockpit Contrats…
      </div>
    );
  }

  const pieByType = (portfolio?.by_type || [])
    .map((t, i) => ({ ...t, color: COLORS[i % COLORS.length] }));
  const totalMRRPortfolio = pieByType.reduce((s, x) => s + (x.mrr || 0), 0);

  const typeLabel = (t) => (t.label || t.name || t.type || "—");

  return (
    <div className="space-y-8" data-testid="contrats-dashboard-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            Vue d'ensemble Contrats
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Pilotage complet — portefeuille, MRR/ARR, reconductions, tendance sur 12 mois.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <AnalyzeAllButton />
          <AutoLinkAllButton partyType="client" onDone={() => setDriftKey((k) => k + 1)} />
        </div>
      </div>

      {/* ═══════════ Zone 1 — À traiter maintenant ═══════════ */}
      <section data-testid="zone-actions">
        <ZoneTitle color="red" label="À traiter maintenant" />
        <div className="space-y-3">
          <RenewalAlertsWidget compact />
          <IndexationAlertsBanner compact />
          <BillingDriftWidget key={driftKey} partyType="client" compact />

          {(overview.to_watch || []).length > 0 && (
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-4">
                  <RefreshCw className="h-5 w-5 text-purple-600" />
                  <h3 className="text-sm font-semibold text-slate-900">
                    Contrats à surveiller
                  </h3>
                  <Badge variant="outline" className="ml-auto bg-purple-50 text-purple-700 border-purple-200">
                    {overview.to_watch_count} en reconduction
                  </Badge>
                </div>
                <div className="space-y-2">
                  {overview.to_watch.slice(0, 5).map((c) => (
                    <motion.div key={c.id}
                                 initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                                 data-testid={`watch-row-${c.id}`}>
                      <Link to={`/app/contracts/${c.id}`}
                             className="flex items-center gap-4 p-3 rounded-lg hover:bg-purple-50 border border-slate-100">
                        {c.reconduit && (
                          <Badge className="bg-purple-100 text-purple-700 border-purple-200">Reconduit</Badge>
                        )}
                        <div className="font-semibold flex-1">{c.title}</div>
                        <div className="text-sm text-slate-500">
                          Fin: <span className="font-mono">{fmtDate(c.end_date)}</span>
                        </div>
                        <div className="font-mono font-semibold text-slate-700">
                          {fmtEUR(c.amount_ht)}
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* ═══════════ Zone 2 — Pilotage ═══════════ */}
      <section data-testid="zone-pilotage">
        <ZoneTitle color="blue" label="Pilotage — portefeuille & financier" />

        {/* 4 Big KPI */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card data-testid="kpi-active"><CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Contrats actifs</div>
            <div className="mt-1 text-3xl font-semibold font-mono">{overview.active || 0}</div>
            <div className="text-[10px] text-slate-400 mt-1">{overview.total || 0} au total</div>
          </CardContent></Card>
          <Card data-testid="kpi-mrr-net"><CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">MRR net</div>
            <div className="mt-1 text-3xl font-semibold font-mono text-blue-700">
              {fmtEUR((kpi.mrr_client || 0) - (kpi.mrr_supplier || 0))}
            </div>
            <div className="text-[10px] text-slate-400 mt-1">
              ARR net {fmtEUR(((kpi.mrr_client || 0) - (kpi.mrr_supplier || 0)) * 12)}
            </div>
          </CardContent></Card>
          <Card data-testid="kpi-mrr-client"><CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">MRR clients</div>
            <div className="mt-1 text-3xl font-semibold font-mono text-emerald-700">
              {fmtEUR(kpi.mrr_client)}
            </div>
            <div className="text-[10px] text-slate-400 mt-1">ARR {fmtEUR(kpi.arr_client)}</div>
          </CardContent></Card>
          <Card data-testid="kpi-mrr-supplier"><CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">MRR fournisseurs</div>
            <div className="mt-1 text-3xl font-semibold font-mono text-amber-700">
              {fmtEUR(kpi.mrr_supplier)}
            </div>
            <div className="text-[10px] text-slate-400 mt-1">ARR {fmtEUR(kpi.arr_supplier)}</div>
          </CardContent></Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mt-4">
          {/* Portefeuille pie chart */}
          <Card className="lg:col-span-5" data-testid="portfolio-pie">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <PieIcon className="h-4 w-4 text-blue-600" />
                <h3 className="text-sm font-semibold text-slate-900">Portefeuille par type</h3>
                <span className="ml-auto text-xs text-slate-500 font-mono">
                  {fmtEUR(totalMRRPortfolio)}/mois
                </span>
              </div>
              {pieByType.length === 0 ? (
                <div className="h-52 flex items-center justify-center text-xs text-slate-400 italic">
                  Aucun contrat actif
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={pieByType} dataKey="mrr" nameKey={typeLabel}
                         cx="50%" cy="50%" outerRadius={80} innerRadius={45}
                         label={(e) => `${typeLabel(e)} ${((e.mrr/(totalMRRPortfolio||1))*100).toFixed(0)}%`}>
                      {pieByType.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => fmtEUR(v)} labelFormatter={typeLabel} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Top 10 tiers */}
          <Card className="lg:col-span-7" data-testid="top-parties">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Users className="h-4 w-4 text-emerald-600" />
                <h3 className="text-sm font-semibold text-slate-900">Top 10 tiers par MRR</h3>
              </div>
              {(financial.top_parties || []).length === 0 ? (
                <div className="text-xs text-slate-400 italic py-8 text-center">Aucun tiers</div>
              ) : (
                <div className="space-y-1.5 max-h-80 overflow-y-auto">
                  {financial.top_parties.map((p, i) => (
                    <div key={i} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded"
                         data-testid={`top-party-${i}`}>
                      <span className="w-6 text-xs font-mono text-slate-400">#{i + 1}</span>
                      <span className="flex-1 font-medium truncate">{p.party_name}</span>
                      <Badge variant="outline" className="text-[10px]">
                        {p.party_type === "client" ? "Client" : "Fournisseur"}
                      </Badge>
                      <span className="text-xs text-slate-500 hidden md:inline">
                        {p.contracts_count} contrat{p.contracts_count > 1 ? "s" : ""}
                      </span>
                      <span className="font-mono font-semibold w-24 text-right">
                        {fmtEUR(p.mrr)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ═══════════ Zone 3 — Tendance & Évolution ═══════════ */}
      <section data-testid="zone-tendance">
        <ZoneTitle color="slate" label="Tendance & évolution" sub="12 mois glissants" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* MRR timeline */}
          <Card className="lg:col-span-7" data-testid="mrr-timeline-chart">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="h-4 w-4 text-blue-600" />
                <h3 className="text-sm font-semibold text-slate-900">MRR net (12 mois)</h3>
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={timeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" fontSize={11} />
                  <YAxis fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => fmtEUR(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="mrr_client" stroke="#10b981" strokeWidth={2} name="Clients" dot={false} />
                  <Line type="monotone" dataKey="mrr_supplier" stroke="#f59e0b" strokeWidth={2} name="Fournisseurs" dot={false} />
                  <Line type="monotone" dataKey="mrr_net" stroke="#3b82f6" strokeWidth={2.5} name="Net" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Créations vs résiliations */}
          <Card className="lg:col-span-5" data-testid="evolution-chart">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Euro className="h-4 w-4 text-slate-600" />
                <h3 className="text-sm font-semibold text-slate-900">Mouvements portefeuille</h3>
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={evolution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" fontSize={11} />
                  <YAxis fontSize={11} allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="created" fill="#10b981" name="Créations" />
                  <Bar dataKey="cancelled" fill="#ef4444" name="Résiliations" />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
